import * as React from 'react'
import { useTranslation, useI18next } from 'gatsby-plugin-react-i18next'
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField } from '@material-ui/core'
import cn from 'classnames'
import FormTextField from 'components/presenter/form/FormTextField'
import FormSelectTextField from 'components/presenter/form/FormSelectTextField'
import { useForm } from 'libs/form/formHook'
import { EmptyValidator } from 'libs/form/validator'
import GoogleMapReact from 'google-map-react'
import FillButton from 'components/presenter/buttons/FillButton'
import Autocomplete from '@material-ui/lab/Autocomplete'
import Form from 'components/presenter/form/Form'
import fetch from 'node-fetch'
import locationData from 'assets/location'
import locationCategoryData from 'assets/locationCategory'
import { useStore, companyStoreId } from 'stores'
import { useSnackbar } from 'libs/ui/snackbar'
import * as styles from './dialog.module.css'

const shopNameId = 'shopName'
const districtId = 'district'
const addressId = 'address'
const shopPhoneId = 'shopPhone'
const geolocationId = 'geolocation'
const locationCategoryId = 'locationCategory'

// TODO: Default Hong Kong geolocation
const center = {
  lat: 22.319305078970952,
  lng: 114.16940017989573
}

const CreateShopDialog = ({open, onClose, onCreate}) => {
  const { t } = useTranslation()
  const companyStore = useStore(companyStoreId)
  const { showError, showSuccess } = useSnackbar()

  const {language} = useI18next()
  const [positionState, setPositionState] = React.useState('disabled')
  const [marker, setMarker] = React.useState(null)
  const [mapInstance, setMapInstance] = React.useState(null)
  const { onChangeValidation, getFormFieldState, isFormReady, requestAsyc, isLoading, formErrorCode } = useForm({
    [shopNameId]: {
      validators: [EmptyValidator],
    },
    [districtId]: {
      validators: [EmptyValidator],
    },
    [locationCategoryId]: {
      validators: [EmptyValidator],
    },
    [addressId]: {
      validators: [EmptyValidator],
    },
    [shopPhoneId]: {
      isOptional: true,
      validators: [EmptyValidator],
    },
    [geolocationId]: {
      validators: [EmptyValidator],
    },
  })

  const getGeoLocation = async (address, region) => {
    const encodedAddress = encodeURIComponent(address)
    const googleApi = `https://maps.googleapis.com/maps/api/geocode/json?address=${encodedAddress}&region=${region}&key=${process.env.GATSBY_GOOGLE_MAP_API}`
    try {
      const res = await fetch(googleApi)
      const data = await res.json()
      if (data && data.results && Array.isArray(data.results) && data.results.length > 0) {
        return data.results[0].geometry
      }
    } catch (err) {
      console.log(err)
      showError(t('shopLocation.failPositioning'))
    }
    return null
  }

  const onFetchGeolocation = async () => {
    setPositionState('loading')
    const location = await getGeoLocation(getFormFieldState(addressId).value, 'hk')
    if (location) {
      mapInstance.setCenter(location.location)
      marker.setPosition(location.location)
      setPositionState('disabled')
      onChangeValidation(geolocationId, location.location)
    } else {
      setPositionState('enabled')
    }
  }

  const renderMarkers = (_map, maps) => {
    const _marker = new maps.Marker({
      position: center,
      map: _map,
    })
    setMarker(_marker)
    setMapInstance(_map)
  }
  const updateMarker = (_map) => {
    const mapCenter = _map.getCenter()
    marker.setPosition(mapCenter)
    onChangeValidation(geolocationId, {
      lat: mapCenter.lat(),
      lng: mapCenter.lng()
    })
  }
  const locationGroup = locationData[language]
  const locationCategoryGroup = locationCategoryData[language]

  React.useEffect(() => {
    setPositionState(getFormFieldState(addressId).value ? 'enabled' : 'disabled')
  }, [getFormFieldState(addressId).value])

  const triggerOnCreate = async () => {
    if (onCreate) {
      const geolocation = getFormFieldState(geolocationId).value
      const response = await requestAsyc(companyStore.actions.createShopLocation({
        name: getFormFieldState(shopNameId).value,
        shortCode: getFormFieldState(districtId).value,
        address: getFormFieldState(addressId).value,
        telephone: getFormFieldState(shopPhoneId).value,
        categoryCode: getFormFieldState(locationCategoryId).value.short_code,
        gelocation: {
          lat: geolocation.lat,
          lng: geolocation.lng,
        },
      }))
      if(!response.error) {
        showSuccess(t('common.success'))
        onCreate()
      }
    }
  }
  return (
    <Dialog
      className={styles.dialog}
      open={open}
      onClose={onClose}
    >
      <DialogTitle className={styles.titleTxt}>{t('shopLocation.createShop')}</DialogTitle>
      <DialogContent>
        <Form className={styles.form} isLoading={isLoading}>
          <FormTextField
            id={shopNameId}
            fieldData={getFormFieldState(shopNameId)}
            className={cn(styles.input)}
            label={t('formFields.shopName')}
            type="text"
            onChange={onChangeValidation}
          />
          <FormSelectTextField
            id={districtId}
            fieldData={getFormFieldState(districtId)}
            className={cn(styles.input)}
            label={t('formFields.district')}
            onChange={onChangeValidation}
          >
            <option aria-label="None" value="" />
            {
              locationGroup.map(group => (
                <optgroup key={group.short_code} label={group.name}>
                  {
                    group.sub_regionals.map(regional => (
                      <option key={regional.short_code} value={regional.short_code}>{regional.name}</option>
                    ))
                  }
                </optgroup>
              ))
            }
          </FormSelectTextField>
          <Autocomplete
            className={cn(styles.input)}
            id={locationCategoryId}
            value={getFormFieldState(locationCategoryId).value}
            options={locationCategoryGroup}
            getOptionLabel={option => option.name}
            filterSelectedOptions
            size="small"
            onChange={(event, newValue) => onChangeValidation(locationCategoryId, newValue)}
            noOptionsText={t('formFields.helper.noFound')}
            renderInput={(params) => (
              <TextField
                {...params}
                className="mui-textfield"
                variant="outlined"
                label={t('formFields.type')}
                placeholder={t('formFields.hint.select')}
              />
            )}
          />
          <FormTextField
            id={addressId}
            fieldData={getFormFieldState(addressId)}
            className={cn(styles.input)}
            label={t('formFields.address')}
            type="text"
            onChange={onChangeValidation}
          />
          <FormTextField
            id={shopPhoneId}
            fieldData={getFormFieldState(shopPhoneId)}
            className={cn(styles.input)}
            label={t('formFields.shopPhone')}
            type="text"
            onChange={onChangeValidation}
          />
          <div className={styles.positionSection}>
            <p className={cn(styles.fieldTitle, styles.titleTxt)}>{t('formFields.shopGeoLocation')}</p>
            <FillButton
              disabled={positionState !== 'enabled'}
              className={cn(styles.btnPin)}
              onClick={onFetchGeolocation}
            >
              {t(positionState === 'loading' ? 'common.loading' : 'shopLocation.getPosition')}
            </FillButton>
          </div>
          <div className={styles.googleMapSection}>
            <GoogleMapReact
              bootstrapURLKeys={{ key: process.env.GATSBY_GOOGLE_MAP_API }}
              defaultCenter={center}
              defaultZoom={16}
              onGoogleApiLoaded={({ map, maps }) => renderMarkers(map, maps)}
              onDrag={updateMarker}
              onDragEnd={updateMarker}
            />
          </div>
        </Form>
      </DialogContent>
      <DialogActions>
        <Button className="mui-dialog-btn-nok" onClick={onClose}>
          {t('common.cancel')}
        </Button>
        <Button className="mui-dialog-btn-ok" onClick={triggerOnCreate} disabled={!isFormReady()}>
          {t('common.create')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default CreateShopDialog